import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_DETAILS_URL } from "@constants/routes";
import { SERVICE_TEMPLATE_ID_PATTERN } from "@constants/url-parameter-patterns";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { TableCellWithButton } from "@containers/common/table-cell-with-button/table-cell-with-button";
import { ButtonSizeTypes } from "@enums/button-size-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedRpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { navigateToUtil } from "@utils/navigation-util";
import { injectCustomIdParameter } from "@utils/parameter-injector";
import { sortStrings } from "@utils/string-util";
import Badge from "react-bootstrap/Badge";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { useMemo } from "react";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";

type ServiceTemplatesTableContainerProps = {
    serviceTemplates: Array<FormattedRpServiceTemplate>;
    tableState: TableState;
    isPageHeight: boolean;
};

type ServiceTemplatesTableDataRow = FormattedRpServiceTemplate & {
    organizationName: string;
};

export const ServiceTemplatesTableContainer = (props: ServiceTemplatesTableContainerProps) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const { serviceTemplates, tableState, isPageHeight } = props;

    const handleViewOnClick = (serviceTemplate: FormattedRpServiceTemplate) => {
        const url = injectCustomIdParameter(
            MANAGEMENT_CONSOLE_MASTER_DATA_SERVICE_TEMPLATES_DETAILS_URL,
            serviceTemplate.id,
            SERVICE_TEMPLATE_ID_PATTERN
        );
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const serviceTemplatesTableData: Array<ServiceTemplatesTableDataRow> = useMemo(() => {
        return serviceTemplates.map(st => ({
            ...st,
            organizationName: st.organization?.name || resources.common.notFound
        }));
    }, [serviceTemplates]);

    const columnHelper = createColumnHelper<ServiceTemplatesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.serviceTemplates.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => (
                <TableCellWithButton
                    id="view-service-template-details-button"
                    size={ButtonSizeTypes.SMALL}
                    variant={"outline-primary"}
                    icon={faEye}
                    text={props.row.original.name}
                    originalObject={props.row.original}
                    onClick={handleViewOnClick}
                />
            ),
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("id", {
            header: resources.tables.serviceTemplates.columns.id.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("organizationName", {
            header: resources.tables.verificationServices.columns.organizationName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
        }),
        columnHelper.accessor("status", {
            header: resources.common.labels.status,
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }                
            }
        })
    ]), [serviceTemplatesTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: serviceTemplatesTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.SERVICE_TEMPLATES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.SERVICE_TEMPLATES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.SERVICE_TEMPLATES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="service-templates"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"service-templates"}
            />
            <PaginationContainer
                idPrefix="service-templates"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
}